import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import calendarService from '../services/calendarService';
import './GoogleCalendarConnect.css';

const GoogleCalendarConnect = ({ onStatusChange }) => {
    const { t } = useTranslation();
    const [status, setStatus] = useState({ connected: false, loading: true });
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const checkStatus = useCallback(async () => {
        try {
            const result = await calendarService.getStatus();
            setStatus({ ...result, loading: false });
            if (onStatusChange) onStatusChange(result);
        } catch (err) {
            setStatus({ connected: false, loading: false });
        }
    }, [onStatusChange]);

    useEffect(() => {
        checkStatus();

        // Check for OAuth callback code in URL
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        if (code) {
            handleCallback(code);
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [checkStatus]);

    const handleCallback = async (code) => {
        setStatus(prev => ({ ...prev, loading: true }));
        setError(null);
        
        try {
            await calendarService.connect(code);
            setSuccess(t('settings.calendarConnected'));
            await checkStatus();
        } catch (err) {
            setError(err.message || t('errors.calendarConnectFailed'));
            setStatus(prev => ({ ...prev, loading: false }));
        }
    };

    const handleConnect = async () => {
        setError(null);
        
        try {
            const { authUrl } = await calendarService.getAuthUrl();
            window.location.href = authUrl;
        } catch (err) {
            setError(err.message || t('errors.calendarConnectFailed'));
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm(t('settings.confirmDisconnect'))) {
            return;
        }
        
        setStatus(prev => ({ ...prev, loading: true }));
        setError(null);
        
        try {
            await calendarService.disconnect();
            setSuccess(t('settings.calendarDisconnected'));
            setStatus({ connected: false, loading: false });
            if (onStatusChange) onStatusChange({ connected: false });
        } catch (err) {
            setError(err.message || t('errors.calendarDisconnectFailed'));
            setStatus(prev => ({ ...prev, loading: false }));
        }
    };

    const handleSync = async () => {
        setSyncing(true);
        setError(null);
        
        try {
            const result = await calendarService.syncBookings();
            setSuccess(t('settings.calendarSynced', { count: result.synced || 0 }));
        } catch (err) {
            setError(err.message || t('errors.calendarSyncFailed'));
        } finally {
            setSyncing(false);
        }
    };

    if (status.loading) {
        return (
            <div className="calendar-connect loading">
                <div className="spinner"></div>
                <span>{t('common.loading')}...</span>
            </div>
        );
    }

    return (
        <div className="calendar-connect">
            <div className="calendar-header">
                <div className="calendar-icon">
                    <svg viewBox="0 0 24 24" width="32" height="32">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                </div>
                <div className="calendar-info">
                    <h3>Google Calendar</h3>
                    <p className={`status ${status.connected ? 'connected' : 'disconnected'}`}>
                        {status.connected ? (
                            <>
                                <span className="status-dot"></span>
                                {t('settings.connected')}
                                {status.email && <span className="email"> ({status.email})</span>}
                            </>
                        ) : (
                            <>
                                <span className="status-dot"></span>
                                {t('settings.notConnected')}
                            </>
                        )}
                    </p>
                </div>
            </div>

            {error && (
                <div className="alert alert-error">
                    <span>⚠️ {error}</span>
                    <button onClick={() => setError(null)}>×</button>
                </div>
            )}

            {success && (
                <div className="alert alert-success">
                    <span>✅ {success}</span>
                    <button onClick={() => setSuccess(null)}>×</button>
                </div>
            )}

            <div className="calendar-description">
                {status.connected ? (
                    <p>{t('settings.calendarConnectedDesc')}</p>
                ) : (
                    <p>{t('settings.calendarDesc')}</p>
                )}
            </div>

            <div className="calendar-actions">
                {status.connected ? (
                    <>
                        <button 
                            className="btn-sync"
                            onClick={handleSync}
                            disabled={syncing}
                        >
                            {syncing ? (
                                <>
                                    <span className="spinner-small"></span>
                                    {t('settings.syncing')}...
                                </>
                            ) : (
                                <>🔄 {t('settings.syncNow')}</>
                            )}
                        </button>
                        <button 
                            className="btn-disconnect"
                            onClick={handleDisconnect}
                        >
                            🔌 {t('settings.disconnect')}
                        </button>
                    </>
                ) : (
                    <button 
                        className="btn-connect"
                        onClick={handleConnect}
                    >
                        <svg viewBox="0 0 24 24" width="20" height="20">
                            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        {t('settings.connectGoogle')}
                    </button>
                )}
            </div>

            {status.connected && status.lastSync && (
                <div className="last-sync">
                    {t('settings.lastSync')}: {new Date(status.lastSync).toLocaleString()}
                </div>
            )}
        </div>
    );
};

export default GoogleCalendarConnect;
